<script setup lang="ts">
import type { components } from '~/types/napis'

const props = defineProps({
  post: {
    type: Object as
      | PropType<components['schemas']['PostResponseSchema']>
      | PropType<components['schemas']['HistoryPostSchema']>,
    required: true
  },
  userId: { type: String, required: true },
  showPin: { type: Boolean, default: false },
  isAdmin: { type: Boolean, default: false },
  isClass: { type: Boolean, default: false },
  hideTags: { type: Boolean, default: false },
  hideFooter: { type: Boolean, default: false },
  isUserAuthor: { type: Boolean, default: false },
  isAuthenticated: { type: Boolean, default: false },
  isBookmarked: { type: Boolean, default: false },
  isSupport: { type: Boolean, default: false }
})

const emit = defineEmits<{
  (e: 'refresh', postId: string | null | undefined): void
}>()

// --------------------
// VIEW MODEL VARIABLES
// --------------------
const name = computed(() => `${props.post.author.first_name} ${props.post.author.last_name}`)
const isShowPin = computed(() => {
  // when to show pin
  // if not a class type post - false in props
  if (!props.isClass) return false
  // if it is a class type post - will be true in props
  // if the post is pinned, then show
  else if (props.post.groups?.includes('PINNED_QUESTION')) return true
  // if post is not pinned, then check user role
  // Only admin can see and interact with pin button
  else return props.isAdmin
})

// Refs
const showPinMenu = ref(false)

// Go to page
const goToPage = () => {
  if (
    props.post.groups?.includes('ANNOUNCEMENT') &&
    props.post.tags &&
    props.post.tags.length > 0
  ) {
    navigateTo(
      `/forum/class-forum/${props.post.tags[0].slug_name}/announcements/?highlight=${props.post.id_slug}#highlight`
    )
  } else {
    navigateTo(`/forum/post/${props.post.id_slug}`)
  }
}

const isPostAuthorAdmin = computed(() => {
  if (props.isClass)
    return isAuthorAdmin(props.post.author.user_roles as components['schemas']['UserTypeChoice'][])
  return false
})

const isPostAuthorInstructor = computed(() => {
  if (props.isClass)
    return isAuthorInstructor(
      props.post.author.user_roles as components['schemas']['UserTypeChoice'][]
    )
  return false
})

// --------------------
// FORUM-BACKEND
// --------------------

const { updatePost } = useForumPost()

// Click pin
const clickPin = async () => {
  showPinMenu.value = false

  // only admin can manage pin
  if (props.isAdmin) {
    if (props.post?.groups?.includes('PINNED_QUESTION')) {
      await updatePost(props.post.id_slug, { group_name: [] })
      emit('refresh', props.post.id_slug)
    } else {
      await updatePost(props.post.id_slug, { group_name: ['PINNED_QUESTION'] })
      emit('refresh', props.post.id_slug)
    }
  }
}

const clickPinBtn = () => {
  if (props.isAdmin && props.isAuthenticated) {
    showPinMenu.value = !showPinMenu.value
  }
}
</script>

<template>
  <div
    :class="`post-card card-style clickable ${hideFooter ? 'hide-footer' : ''}`"
    @click="goToPage"
  >
    <div class="post-card-header">
      <div class="post-card-header-author">
        <div class="post-card-header-avatar">
          <PageForumAvatarInitials
            :image="post.author.profile_image?.presigned_get_object_url || undefined"
            :first-name="post.author.first_name || ''"
            :last-name="post.author.last_name || ''"
          />
        </div>

        <div class="post-card-header-text">
          <div class="post-card-header-name max-one-line">
            <span>{{ name }}</span>
            <b v-if="isPostAuthorAdmin || isPostAuthorInstructor">·</b>
            <span v-if="isPostAuthorAdmin" class="green">Admin</span>
            <span v-if="isPostAuthorInstructor" class="green">Instructor</span>
          </div>
          <div class="post-card-header-time">posted {{ getTimeFromToday(post.created_date) }}</div>
        </div>
      </div>

      <div class="post-card-header-btns">
        <div
          v-if="
            isShowPin &&
            !props.post.groups?.includes('SUPPORT') &&
            !props.post.groups?.includes('ANNOUNCEMENT')
          "
          :class="`post-menu-btn btn-pin ${post?.groups?.includes('PINNED_QUESTION') ? 'btn-pinned' : ''}`"
          @click.stop="clickPinBtn"
        >
          <CommonButton
            square
            :grey="!post?.groups?.includes('PINNED_QUESTION')"
            :outline="post?.groups?.includes('PINNED_QUESTION')"
          >
            <CommonIconForumPin />
          </CommonButton>

          <CommonMenuDropdown :show-menu="showPinMenu" @close-menu="showPinMenu = false">
            <div @click.stop="clickPin">
              <span>{{
                post?.groups?.includes('PINNED_QUESTION') ? 'Remove pin' : 'Pin post'
              }}</span>
            </div>
          </CommonMenuDropdown>
        </div>

        <div class="menu-btn">
          <PageForumPostMenu
            :post-id="post.id_slug"
            :post-title="post.title"
            :is-pinned="post?.groups?.includes('PINNED_QUESTION') || false"
            :is-resolved="post.is_resolved || false"
            :is-announcement="post.groups?.includes('ANNOUNCEMENT') || false"
            :is-admin="isAdmin"
            :is-author="isUserAuthor"
            :is-bookmark="post.is_bookmarked"
            :is-follow="post.followed_post || false"
            :is-authenticated="isAuthenticated"
            :is-classroom-post="post.groups?.includes('CLASSROOM_POST') || false"
            :user-id="userId"
            :is-support="isSupport"
            @refresh-list="() => emit('refresh', undefined)"
            @refresh-post="() => emit('refresh', post.id_slug)"
          />
        </div>
      </div>
    </div>

    <div class="post-card-content">
      <div class="post-card-title">
        <h4 class="max-two-line">{{ post.title }}</h4>
        <div v-if="post.is_resolved" class="post-resolved">
          <span>Resolved</span>
          <CommonIconCheck />
        </div>
      </div>

      <!-- Image -->
      <div v-if="post.image" class="img-wrapper">
        <img
          v-if="post.image"
          class="main-img"
          :src="post.image.presigned_get_object_url || undefined"
        >
        <img
          v-if="post.image"
          class="bg-img"
          :src="post.image.presigned_get_object_url || undefined"
        >
        <div v-else class="error-msg">Media error</div>
      </div>

      <!-- Text -->
      <div
        v-if="post.description"
        class="post-card-content-description max-two-line"
        v-html="post.description"
      />
    </div>

    <div v-if="!hideTags" class="post-card-tags">
      <PageForumTagPillList :tags="post?.tags || []" :post-id="post.id_slug" />
    </div>
    <PageForumPostFooter
      v-if="!hideFooter"
      :upvote-count="post.upvote_count || 0"
      :downvote-count="post.downvote_count || 0"
      :comments="post.comment_reply_count || 0"
      :is-upvoted="post.vote_value === 1 || false"
      :is-downvoted="post.vote_value === -1 || false"
      :is-follow="post.followed_post || false"
      :post-id="post.id_slug"
      :is-admin="isAdmin"
      :is-author="isUserAuthor"
      :is-authenticated="isAuthenticated"
      :user-id="userId"
      :is-resolved="post.is_resolved || false"
      :is-announcement="post.groups?.includes('ANNOUNCEMENT') || false"
      @refresh="() => emit('refresh', post.id_slug)"
    />
  </div>
</template>

<style scoped lang="scss">
.post-card {
  padding: 25px;

  &-header {
    display: flex;
    align-items: center;
    justify-content: space-between;

    $avatarSize: 40px;

    &-author {
      display: grid;
      grid-template-columns: $avatarSize auto auto;
      gap: 15px;
      align-items: center;
    }

    &-avatar {
      height: $avatarSize;
      width: $avatarSize;

      :deep(span) {
        font-size: 1.1rem;
      }
    }

    &-name {
      font-size: 0.87rem;
      line-height: 0.87rem;
      margin-bottom: 5px;
      font-weight: 500;

      b {
        padding: 0 7px;
      }

      .green {
        color: var(--forumBlue);
      }
    }

    &-time {
      font-size: 0.85rem;
      line-height: 0.85rem;
      color: var(--textM2);
    }

    &-btns {
      display: flex;
      align-items: center;
      gap: 5px;

      .post-menu-btn {
        position: relative;
        transition:
          opacity 0.3s ease-out,
          width 0.3s ease-out;
      }

      .btn-pin {
        :deep(svg) {
          height: 16px;
          width: 16px;
        }
      }
    }
  }

  &-content {
    margin: 20px 0 40px;

    h4 {
      font-size: 1.25rem;
      line-height: 1.8rem;
      font-weight: 500;
      display: inline;
      margin-right: 12px;
    }

    &-description {
      margin-top: 5px;
      color: var(--textM2);
      line-height: 1.3rem;
      font-size: 0.95rem;

      :deep(p),
      :deep(strong),
      :deep(h4) {
        font-weight: 400;
      }
    }

    .img-wrapper {
      width: 100%;
      margin-top: 15px;
      overflow: hidden;
      border-radius: 10px;
      border: 1px solid var(--borderL2);
      position: relative;
      background-color: gainsboro;
      min-height: 10dvh;
    }

    img {
      width: 100%;
      max-height: 40dvh;
    }

    .main-img {
      object-fit: contain;
      z-index: 2;
      isolation: isolate;
      position: relative;
    }

    .bg-img {
      object-fit: cover;
      filter: blur(30px);
      -webkit-filter: blur(30px);
      opacity: 0.4;
      position: absolute;
      top: 0;
      left: 0;
      user-select: none;
    }

    .error-msg {
      text-align: center;
      position: absolute;
      display: flex;
      align-items: center;
      justify-content: center;
      top: 0;
      right: 0;
      height: 100%;
      width: 100%;
      font-weight: 500;
      opacity: 0.3;
      font-size: 0.9rem;
    }
  }

  &-tags {
    margin-bottom: 20px;
  }
}

.post-resolved {
  white-space: nowrap;
  height: 25px;
  display: inline-flex;
  align-items: center;
  font-size: 0.83rem;
  background: var(--forumLightGreen);
  padding: 0 10px;
  border-radius: 7px;
  font-weight: 500;
  color: var(--forumGreen);
  transform: translateY(-2px);

  svg {
    height: 10px;
    width: 10px;
    margin-left: 5px;
    fill: var(--forumGreen);
  }
}

.hide-footer {
  .post-card-tags {
    margin-bottom: 5px;
  }
}

@media (max-width: 600px) {
  .post-card {
    margin: 0 -20px;
    padding: 25px 20px 10px;
    border: none;
    border-top: 1px solid var(--borderM2);
    box-shadow: none;
    border-radius: 0;

    &-content {
      .img-wrapper {
        min-height: 10vh;
      }

      img {
        max-height: 40vh;
      }
    }
  }
}

@media (hover: hover) {
  .post-card {
    &-header {
      &-btns {
        .post-menu-btn {
          opacity: 0;
          width: 0;
        }

        .btn-pinned {
          width: auto;
          opacity: 1;
        }
      }
    }

    :deep(.post-card-footer-btn) {
      opacity: 0;
    }
  }

  .post-card:hover {
    box-shadow: 0 5px 20px rgba(212, 216, 225, 0.5);

    .post-card {
      &-header {
        &-btns {
          .post-menu-btn {
            opacity: 1;
            width: 30px;
          }
        }
      }
    }

    :deep(.post-card-footer-btn) {
      opacity: 1;
    }
  }
}
</style>
